/* eslint-disable import/no-cycle */
import * as React from 'react';
import { TabStyles } from '../../styles/TabStyles';
import { autoFocus } from '../../utils/AutoFocus';
import { makeTestId } from '../../utils/Declarative';
import { DialogBody, getBodyPaddingStyle } from './DialogBody';
import { DialogFooter } from './DialogFooter';
import { DialogHeader } from './DialogHeader';
import { DialogContentProps, DialogType } from './DialogWidget';

const DialogContentStyles: React.CSSProperties = {
  boxSizing: 'border-box',
  color: TabStyles.Typography.Color.Primary,
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  outline: 'none',
  overflow: 'hidden',
};

export class DialogContent extends React.Component<DialogContentProps, {}> {
  private dialogElement: React.RefObject<HTMLDivElement> = React.createRef();
  private elementToReturnFocus: HTMLElement | null = null;
  private focusEventListener: EventListener;
  private sawESCkeyDown: boolean = false; // only handle ESC keyUp when dialog previously got ESC keyDown

  private handleDialogKeyUp = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape' && this.sawESCkeyDown) {
      this.sawESCkeyDown = false;
      this.props.onCloseRequest();
    }
  }

  private handleDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      // eat Escape keyDown since we'll handle it in keyUp
      this.sawESCkeyDown = true;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }

  public componentDidMount(): void {
    // Initially set the element to return focus to as the last active element.
    this.elementToReturnFocus = document.activeElement as HTMLElement;

    this.initializeFocusOnMount();

    this.setFocusEventListener();
    document.addEventListener('focus', this.focusEventListener, true);

    // IE11 column-based flex containers need an explicit height to size flex children correctly. The symptom that we want to avoid
    // is when the content overflows the floater rather than shrinking, so apply this hack only if that happens.
    const dialogElement = this.dialogElement.current!;
    if (dialogElement.offsetHeight > dialogElement.parentElement!.clientHeight) {
      dialogElement.style.height = `${dialogElement.parentElement!.clientHeight}px`;
    }
  }

  private initializeFocusOnMount(): void {
    autoFocus(this.props.disableAutoFocus, this.dialogElement.current!, this.hasHeader && this.props.showCloseButton);
  }

  /**
   * If the dialog is nonModal it should keep track of the last element focused outside the dialog to be focused so when the dialog is
   * closed the focus can return to the last focused element.
   */
  private setFocusEventListener(): void {
    this.focusEventListener = (event: Event) => {
      if (!this.props.isModal) {
        const target = event.target as HTMLElement;
        if (!this.dialogElement.current!.contains(target)) {
          this.elementToReturnFocus = target;
        }
      }
    };
  }

  public componentWillUnmount(): void {
    document.removeEventListener('focus', this.focusEventListener, true);

    // For non-modal dialog, we need to return focus to the most recently focused element outside of the dialog,
    // whereas for modal dialog, the return focus behavior is already provided by `ReactFocusLock`.
    if (this.props.shouldReturnFocusOnClose && !this.props.isModal) {
      this.elementToReturnFocus?.focus();
    }
  }

  private get hasHeader(): boolean {
    return this.props.type !== DialogType.Uber;
  }

  public render(): JSX.Element {
    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <div
        aria-label={this.props.title}
        data-tb-test-id={makeTestId(this.props.testId, 'Content')}
        onKeyDown={this.handleDialogKeyDown}
        onKeyUp={this.handleDialogKeyUp}
        ref={this.dialogElement}
        role='dialog'
        style={{
          ...DialogContentStyles,
          ...this.props.dialogContentStyle
        }}
        tabIndex={-1}
      >
        {this.hasHeader && <DialogHeader {...this.props} />}
        <DialogBody
          children={this.props.children}
          dialogBodyStyle={this.props.dialogBodyStyle}
          showFooter={!!this.props.footerContent}
          testId={this.props.testId}
          type={this.props.type}
        />
        {!!this.props.footerContent &&
          <DialogFooter
            children={this.props.footerContent}
            sidePadding={getBodyPaddingStyle(this.props.type, true).paddingRight}
            testId={this.props.testId}
          />
        }
      </div>
    );
  }
}
